/**
 * LearnerStateHome — dumb consumer of GET /api/learner-state/home.
 *
 * §23 Step 6 (May 2026) — the frontend replacement for the
 * previously-direct mount of <CalmLearnerDashboard>. From now on,
 * the engine is the source of truth: the consumer reads the wire
 * payload and renders the 5 slot wrappers in the fixed order
 * locked by spec §5.
 *
 * Step 6 scope:
 *   • Spine is the only populated slot (Step 5 wire contract).
 *   • Other four slots return `null` → the consumer renders nothing
 *     in their wrappers. Calm baseline (§18).
 *   • All existing CalmLearnerDashboard surfaces stay mounted below
 *     the engine spine so the home stays visually near-identical
 *     until Steps 8–15 progressively migrate them.
 *
 * Doctrine baked in:
 *   • Spec §21 — frontend is dumb:
 *       - No per-slot fetches.
 *       - No client-side priority logic.
 *       - No client-side tone or eligibility rules.
 *       - No copy invented or rewritten by the client.
 *   • Spec §11 — refresh cadence:
 *       - On mount.
 *       - On window focus.
 *       - Every 60 s while mounted.
 *   • Spec §21 fallback contract:
 *       - Cache last successful payload in `sessionStorage`
 *         (`learner_state_home_cached_v1`).
 *       - On 503 / network error → render cached, NO error UI.
 *       - On no-cache + failure → render bottom nav (handled by
 *         LearnerHub) + a quiet "Loading…" placeholder. No retry
 *         button, no error dialog.
 *   • Spec §22 #16 — no client-side rule logic. Ever.
 *
 * The authority statement (Step 4) is now sourced from this
 * payload's `authority_statement` field, NOT from the separate
 * /api/learner-state/authority-statement endpoint. The endpoint
 * stays alive for back-compat; the CalmLearnerDashboard no longer
 *   - The dumb consumer reads the wire payload and renders slots
 *     verbatim. See LEARNER_STATE_ENGINE_SPEC.md §21.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

import SlotCard from "./SlotCard";
import ContinuityRibbon from "./ContinuityRibbon";
import CalmLearnerDashboard from "../../pages/CalmLearnerDashboard";

const API = process.env.REACT_APP_BACKEND_URL;
const CACHE_KEY = "learner_state_home_cached_v1";
const REFRESH_INTERVAL_MS = 60_000;

// Fixed order — locked by spec §5. The frontend does NOT
// reorder; it iterates this list and renders whatever the
// backend put in each slot (or nothing).
const SLOT_ORDER = [
  "right_now",
  "from_your_last_lesson",
  "where_you_are",
  "focus_today",
  "reflection_invite",
];

function loadCachedPayload() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveCachedPayload(payload) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(payload));
  } catch {
    // Storage may be unavailable (private mode, quota). Silent
    // failure is correct here per §21.
  }
}

export default function LearnerStateHome({
  showTrackOnboarding,
  isIndependent,
  trackState,
  onTrackAnswered,
}) {
  // Hydrate from sessionStorage so the first render is calm if a
  // cached payload exists. `null` means "first ever load" — the
  // bottom of the file renders a minimal "Loading…" placeholder.
  const [payload, setPayload] = useState(loadCachedPayload);
  const [loadFailed, setLoadFailed] = useState(false);
  const mountedRef = useRef(true);

  const fetchHome = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/learner-state/home`, {
        withCredentials: true,
      });
      if (!mountedRef.current) return;
      if (res?.data && typeof res.data === "object") {
        setPayload(res.data);
        setLoadFailed(false);
        saveCachedPayload(res.data);
      }
    } catch {
      // §21 — render cached payload, no error UI.
      if (mountedRef.current) setLoadFailed(true);
    }
  }, []);

  // Refresh: on mount + on focus + every 60s.
  useEffect(() => {
    mountedRef.current = true;
    fetchHome();
    const id = setInterval(fetchHome, REFRESH_INTERVAL_MS);
    const onFocus = () => fetchHome();
    window.addEventListener("focus", onFocus);
    return () => {
      mountedRef.current = false;
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, [fetchHome]);

  // Spine signal_key may change between fetches; locally dropping
  // a non-spine signal_key after a dismiss is handled in <SlotCard>
  // via this callback so the immediate next /home call doesn't have
  // to win the race.
  const handleDismissed = useCallback((signalKey) => {
    setPayload((prev) => {
      if (!prev || !prev.slots) return prev;
      const updated = { ...prev, slots: { ...prev.slots } };
      for (const name of SLOT_ORDER) {
        const card = updated.slots[name];
        if (card && card.signal_key === signalKey) {
          updated.slots[name] = null;
        }
      }
      saveCachedPayload(updated);
      return updated;
    });
    // Schedule a fresh fetch so the backend's view re-syncs.
    setTimeout(fetchHome, 250);
  }, [fetchHome]);

  // No payload, no cache → minimal placeholder.
  if (!payload) {
    return (
      <div data-testid="learner-state-home-loading" className="space-y-5">
        <p className="text-sm text-slate-400 dark:text-slate-500">Loading…</p>
        <CalmLearnerDashboard
          showTrackOnboarding={showTrackOnboarding}
          isIndependent={isIndependent}
          trackState={trackState}
          onTrackAnswered={onTrackAnswered}
        />
      </div>
    );
  }

  const slots = payload.slots || {};
  const authorityStatement =
    (payload.authority_statement || "").trim() || null;
  // Phase 1 — Pupil L3 Break #4 (Feb 28, 2026): the engine
  // `where_you_are` slot is the canonical "where am I" surface.
  // When it is populated, the legacy framework strip's Card 1
  // ("Current focus" — stage name + progress bar) must NOT duplicate
  // the same orientation truth. Cards 2/3/4 (Why / Next Action /
  // What this Unlocks) and the "Recent gain" line remain — they are
  // supporting continuity, not primary orientation.
  const suppressPrimaryOrientation = Boolean(slots.where_you_are);
  // Phase 5 — Operational Continuity Convergence v2 (Feb 28, 2026).
  // When the engine's `from_your_last_lesson` slot is populated,
  // that slot owns the backward-looking "what did we last work on"
  // psychological question. The HomeworkContinuityBridge below
  // should defer its "Last time:" row silently. Its "This time"
  // row and the forward anchor still render — they are unique
  // forward-continuity signals the bridge owns. Doctrine:
  // "If two surfaces answer the same psychological question, one
  //  of them should defer silently."
  const suppressBridgeLastTime = Boolean(slots.from_your_last_lesson);

  return (
    <div
      data-testid="learner-state-home"
      data-mode={payload.mode}
      data-track={payload.track}
      data-load-failed={loadFailed ? "1" : "0"}
      className="space-y-5"
    >
      {/* Persistent authority statement (Step 4 surface, now sourced
          from the aggregator response). One quiet line, no chrome. */}
      {authorityStatement && (
        <p
          data-testid="learner-authority-statement"
          className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed"
        >
          {authorityStatement}
        </p>
      )}

      {/* Engine slots — fixed order per spec §5. Render only
          populated slots. Null slots produce no DOM (§18). */}
      {SLOT_ORDER.map((name) => {
        const card = slots[name];
        if (!card) return null;
        return (
          <SlotCard
            key={`${name}:${card.signal_key}`}
            payload={card}
            onDismissed={handleDismissed}
          />
        );
      })}

      {/* §23 Step 15 — Continuity ribbon. Silent when empty;
          renders the last 3 confirmation-receipt events. */}
      <ContinuityRibbon items={payload.recent_continuity} />

      {/* Legacy calm surfaces — preserved at Step 6 for visual
          continuity. Steps 8–15 progressively migrate these into
          engine-driven slots and then unmount the fossils. */}
      <CalmLearnerDashboard
        showTrackOnboarding={showTrackOnboarding}
        isIndependent={isIndependent}
        trackState={trackState}
        onTrackAnswered={onTrackAnswered}
        suppressPrimaryOrientation={suppressPrimaryOrientation}
        suppressBridgeLastTime={suppressBridgeLastTime}
      />
    </div>
  );
}
